import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Compass, LogOut, Users, CalendarDays, Ban, Trash2, RotateCcw, Search, Eye, UserCheck, MessageSquareHeart,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { topicLabel, slotTime } from "@/i18n";

const StatCard = ({ icon, label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">{icon} {label}</div>
    <div className="text-3xl font-semibold text-slate-800">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, spanish: 0, english: 0, cancelled: 0, visits: 0, unique_visitors: 0, reviews: 0 });
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [b, s, r] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
        api.get("/admin/reviews"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setReviews(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("ft_admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem("ft_admin_token")) {
      navigate("/admin");
      return;
    }
    load();
  }, []);

  const logout = () => {
    sessionStorage.removeItem("ft_admin_token");
    navigate("/admin");
  };

  const setStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success(status === "cancelled" ? "Reserva cancelada" : "Reserva reactivada");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta reserva de forma permanente?")) return;
    await api.delete(`/admin/bookings/${id}`);
    toast.success("Reserva eliminada");
    load();
  };

  const removeReview = async (id) => {
    if (!window.confirm("¿Eliminar esta opinión?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Opinión eliminada");
    load();
  };

  const filtered = bookings.filter((b) => {
    const q = query.toLowerCase();
    return (
      b.name?.toLowerCase().includes(q) ||
      b.email?.toLowerCase().includes(q) ||
      b.date?.includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 relative z-10">
      <header className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <Compass className="text-coastal-accent" size={22} />
          <span className="font-heading text-lg">Free Tour Torrevieja</span>
          <Badge className="ml-2 bg-slate-700 text-slate-200">Admin</Badge>
        </div>
        <Button
          data-testid="admin-logout-btn"
          onClick={logout}
          variant="ghost"
          className="text-slate-300 hover:text-white hover:bg-slate-800"
        >
          <LogOut size={16} className="mr-1.5" /> Salir
        </Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-semibold text-slate-800 mb-6">Reservas</h1>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard testid="stat-visits" icon={<Eye size={15} />} label="Visitas totales" value={stats.visits} />
          <StatCard testid="stat-unique" icon={<UserCheck size={15} />} label="Visitantes únicos" value={stats.unique_visitors} />
          <StatCard testid="stat-reviews" icon={<MessageSquareHeart size={15} />} label="Opiniones" value={stats.reviews} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard testid="stat-total" icon={<Users size={15} />} label="Reservas activas" value={stats.total} />
          <StatCard testid="stat-es" icon={<CalendarDays size={15} />} label="Español" value={stats.spanish} />
          <StatCard testid="stat-en" icon={<CalendarDays size={15} />} label="Inglés" value={stats.english} />
          <StatCard testid="stat-it" icon={<CalendarDays size={15} />} label="Italiano" value={stats.italian} />
          <StatCard testid="stat-cancelled" icon={<Ban size={15} />} label="Canceladas" value={stats.cancelled} />
        </div>

        <div className="relative mb-4 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="admin-search"
            placeholder="Buscar por nombre, email o fecha"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-white border-slate-200"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="bookings-table">
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Personas</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Turno</TableHead>
                  <TableHead>Idioma</TableHead>
                  <TableHead>Intereses</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Cargando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400" data-testid="no-bookings">Sin reservas</TableCell></TableRow>
                ) : (
                  filtered.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className={b.status === "cancelled" ? "opacity-50" : ""}>
                      <TableCell className="font-medium">
                        {b.name}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        <div>{b.email}</div>
                        <div>{b.phone}</div>
                      </TableCell>
                      <TableCell className="text-center font-semibold">{b.party_size || 1}</TableCell>
                      <TableCell>{b.date}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {slotTime(b.slot)}
                      </TableCell>
                      <TableCell><Badge variant="outline">{b.language === "es" ? "ES" : "EN"}</Badge></TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="flex flex-wrap gap-1">
                          {(b.topics || []).map((tp) => (
                            <span key={tp} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              {topicLabel(tp, "es")}
                            </span>
                          ))}
                        </div>
                        {b.custom_topic && <div className="text-[11px] text-slate-400 mt-1 italic">"{b.custom_topic}"</div>}
                      </TableCell>
                      <TableCell>
                        {b.status === "cancelled" ? (
                          <Badge className="bg-red-100 text-red-600">Cancelada</Badge>
                        ) : b.status === "pending" ? (
                          <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">Confirmada</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {b.status === "cancelled" ? (
                          <Button size="sm" variant="ghost" data-testid={`reactivate-${b.id}`} onClick={() => setStatus(b.id, "confirmed")} className="text-green-600">
                            <RotateCcw size={15} />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" data-testid={`cancel-${b.id}`} onClick={() => setStatus(b.id, "cancelled")} className="text-amber-600">
                            <Ban size={15} />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" data-testid={`delete-${b.id}`} onClick={() => remove(b.id)} className="text-red-500">
                          <Trash2 size={15} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-slate-800 mt-10 mb-4 flex items-center gap-2">
          <MessageSquareHeart size={20} className="text-coastal-accent" /> Opiniones
        </h2>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <p className="text-slate-400" data-testid="no-reviews">Aún no hay opiniones.</p>
          ) : (
            reviews.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-800">{r.name || "Anónimo"}</p>
                    <p className="text-xs text-slate-400">
                      {(r.language || "es").toUpperCase()} · {r.created_at ? r.created_at.slice(0, 10) : ""}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" data-testid={`delete-review-${r.id}`} onClick={() => removeReview(r.id)} className="text-red-500 shrink-0">
                    <Trash2 size={15} />
                  </Button>
                </div>
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{r.message}</p>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
